// Per-user notification preferences (notification-service, via the gateway).
// One row per category × channel; `required` channels are always on and the
// server ignores attempts to turn them off. Every signed-in user may read and
// write their own preferences — no tenant permission involved.
import { gatewayApi } from '@/lib/hrApi';

/**
 * `web_push` is a DESKTOP browser notification — a sibling of `push`, not the
 * same switch. Push means the app on your phone; web_push means this browser
 * while it is open.
 */
export type NotificationChannel = 'in_app' | 'push' | 'email' | 'web_push';
export const NOTIFICATION_CHANNELS: NotificationChannel[] = ['in_app', 'push', 'email', 'web_push'];

export interface NotificationChannelPref { enabled: boolean; required: boolean }
export interface NotificationCategoryPrefs {
  key: string;
  label: string;
  description?: string | null;
  channels: Record<NotificationChannel, NotificationChannelPref>;
}
export interface NotificationPreferences { categories: NotificationCategoryPrefs[] }
export interface NotificationPreferenceUpdate {
  category: string;
  channel: NotificationChannel;
  enabled: boolean;
}

const PREFS = '/api/v1/notifications/preferences';

export const getNotificationPreferences = async (): Promise<NotificationPreferences> => {
  const d = (await gatewayApi.get<Partial<NotificationPreferences>>(PREFS)).data || {};
  return { categories: d.categories || [] };
};

/** Partial update — only the rows sent change; returns the full preference set. */
export const updateNotificationPreferences = async (
  preferences: NotificationPreferenceUpdate[],
): Promise<NotificationPreferences> => {
  const d = (await gatewayApi.put<Partial<NotificationPreferences>>(PREFS, { preferences })).data || {};
  return { categories: d.categories || [] };
};

// ---- Tenant notification policy (admin) ----
// Per category × channel the tenant decides `required` (always on, locked for
// everyone), `optional` (each person chooses in their own settings) or `off`.
// Cells the platform locks (`locked: true`) cannot be changed by the tenant.
// PUT is tenant-admin only (403 with a plain `message` otherwise).
export type NotificationPolicyMode = 'required' | 'optional' | 'off';
export const NOTIFICATION_POLICY_MODES: NotificationPolicyMode[] = ['required', 'optional', 'off'];
export interface NotificationPolicyChannel { mode: NotificationPolicyMode; locked: boolean }
export interface NotificationPolicyCategory {
  key: string;
  label: string;
  description?: string | null;
  channels: Record<NotificationChannel, NotificationPolicyChannel>;
}
export interface NotificationPolicy { categories: NotificationPolicyCategory[] }
export interface NotificationPolicyUpdate {
  category: string;
  channel: NotificationChannel;
  mode: NotificationPolicyMode;
}

const POLICY = '/api/v1/notifications/policy';

export const getNotificationPolicy = async (): Promise<NotificationPolicy> => {
  const d = (await gatewayApi.get<Partial<NotificationPolicy>>(POLICY)).data || {};
  return { categories: d.categories || [] };
};

/** Partial update — only the rows sent change; returns the full policy. */
export const updateNotificationPolicy = async (policies: NotificationPolicyUpdate[]): Promise<NotificationPolicy> => {
  const d = (await gatewayApi.put<Partial<NotificationPolicy>>(POLICY, { policies })).data || {};
  return { categories: d.categories || [] };
};

// ---- Email delivery trail (admin) ----
//
// "Was an email sent for this, and if not why". Before this existed a
// successful send left a dedup marker, a suppressed one left a log line and a
// failed one left a different log line — so "the manager never got the email"
// could not be traced to which had happened, and logs roll.

export type EmailDeliveryStatus = 'sent' | 'failed' | 'suppressed' | 'deduped';

export interface EmailDelivery {
  id: string;
  recipient_email: string;
  subject?: string;
  status: EmailDeliveryStatus;
  /** Why it failed, or why it was suppressed. */
  error?: string;
  event_type?: string;
  event_id?: string;
  correlation_id?: string;
  environment?: string;
  created_at: string;
}

export interface EmailDeliveryTrail {
  deliveries: EmailDelivery[];
  /** The environment the notification service is running in. Part of the
   *  answer, not decoration: outside production a "suppressed" row is the
   *  delivery guard working, and a reader who does not know which environment
   *  they are looking at will read it as a fault. */
  environment: string;
}

export const getEmailDeliveries = async (params?: {
  recipient?: string;
  status?: EmailDeliveryStatus;
  limit?: number;
}): Promise<EmailDeliveryTrail> => {
  const d = (
    await gatewayApi.get<Partial<EmailDeliveryTrail>>('/api/v1/notifications/email-deliveries', {
      params,
    })
  ).data || {};
  return { deliveries: Array.isArray(d.deliveries) ? d.deliveries : [], environment: d.environment || '' };
};
